import React, { useState, useEffect } from "react";
import HsnFinder from "@/component/shared/hsn/hsnFindercommon";
import { ENDPOINTS, WEB_URL } from "@/config/endpoints";
import axios from "axios";
import Link from "next/link";
import dynamic from 'next/dynamic';
import { FormProvider } from '@/component/shared/contactForm/formContext';
import Head from 'next/head';
import { getHSNSearchBreadcrumbSchema } from "@/lib/hsnSearchBreadcrumbSchema";
import HSNsearch_config from "@/config/hsnSearch_config";

const ContactFormStep1 = dynamic(() => import('@/component/shared/contactForm/contactFormStep1'), {
  loading: () => <p>Loading...</p>
})
const ContactFormStep2 = dynamic(() => import('@/component/shared/contactForm/contactFormStep2'), {
  loading: () => <p>Loading...</p>
})

const Search = ({ hsnInfo, total }) => {
  const [showNextStep, setShowNextStep] = useState(false);
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(2);
  const [hsnList, setHsnList] = useState(hsnInfo);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(hsnInfo.length < total);
  const [initialLoading, setInitialLoading] = useState(true);

  const breadcrumbSchema = getHSNSearchBreadcrumbSchema();

  const showSteps = () => {
    setShowNextStep(true)
  }
  const showFirstStep = () => {
    setShowNextStep(false)
  }
  useEffect(() => {
    setInitialLoading(false);
  }, []);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await axios.get(ENDPOINTS.HSN_KEYWORDS_LIST_API, {
        params: {
          page: page,
          limit: PAGE_SIZE,
        },
      });
      const newRows = (data?.data || []).filter((item) => !HSNsearch_config.includes(item.slug));
      const apiTotal = data?.totalCount || 0;

      if (newRows.length === 0) {
        setHasMore(false);
        return;
      }
      setHsnList(prev => {
        const updated = [...prev, ...newRows];
        if (updated.length >= apiTotal) {
          setHasMore(false);
        }
        return updated;
      });
      setPage(prev => prev + 1);
    } catch (err) {
      console.error("Load More Error:", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const sortedHsnList = hsnList
    .filter((item) => !HSNsearch_config.includes(item.slug))
    .filter(
      (item, index, self) =>
        index === self.findIndex(h => h.keyword === item.keyword)
    ).sort((a, b) => {
      const countA = a.keyword.trim().split(/\s+/).length;
      const countB = b.keyword.trim().split(/\s+/).length;
      if (countA !== countB) {
        return countA - countB;
      }
      return a.keyword.localeCompare(b.keyword);
    });

  return (
    <main>
      <Head>
        <title>{'List of HSN Codes by Keywords'}</title>
        <meta name="description" content={'Browse HSN Codes by popular keywords and get GST rates.'} />
        {/* OpenGraph tags */}
        <meta property="og:title" content={'List of HSN Codes by Keywords'} />
        <meta property="og:description" content={'Browse HSN Codes by popular keywords and get GST rates.'} />
        <meta property="og:url" content={WEB_URL + '/hsn-code/search'} />
        <link rel="canonical" href={WEB_URL + '/hsn-code/search'} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      </Head>
      <HsnFinder />
      <section className="bg-white py-20">
        <div className="container px-3 xm:px-2 lg:px-3">
          <div className="flex flex-wrap gap-1">
            <div className="w-[62%] md:w-full sm:w-full pr-4 sm:pr-0 md:pr-0">
              <div className="mb-5">
                <p className="text-sm text-secondary mb-3">
                  <Link href={WEB_URL} rel="noopener noreferrer">
                    <span
                      className="text-[#2369d8] cursor-pointer font-sm">
                      {" "}
                      Home {"  "}
                    </span>
                  </Link>
                  {"  "}{" > "}
                  <span
                    className="font-sm">
                    {" "}
                    HSN Codes List
                  </span>
                </p>
                <h1 className="text-xl font-medium text-secondary sm:text-base">
                  Browse HSN Codes by Keywords
                </h1>
              </div>

              <div className="mt-8 flow-root">
                <div className="overflow-x-auto flex flex-wrap">
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                    <ul className="flex flex-wrap">
                      {sortedHsnList.map((hsn) => (
                        <li
                          key={hsn.slug}
                          className="first:pl-0 relative text-base after:content-['|'] after:px-2 sm:after:px-[5px] last:after:content-[''] after:text-base mb-2 capitalize"
                        >
                          <Link
                            className="text-secondary hover:text-yellowcolor hover:underline"
                            href={WEB_URL + `/hsn-code/search/${hsn.slug}`}
                          >
                            {hsn.keyword}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              {!initialLoading && hasMore && hsnList.length < total && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md 
                       ${loadingMore ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-[#022B5C] to-[#0363C4] hover:from-[#0363C4] hover:to-[#022B5C]'}`}
                  >
                    {loadingMore ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        Loading...
                      </span>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}

            </div>
            <div className="w-[37%] md:w-full sm:w-full">
              <div className='lg:sticky xm:sticky md:mt-5 lg:top-[130px] xm:top-[100px]'>
                <div className='mb-1'>
                  <FormProvider>
                    {!showNextStep ?
                      <ContactFormStep1 showStep1={showSteps} /> :
                      <ContactFormStep2 showPrevStep={showFirstStep} formType="GetInTouchNew" />
                    }
                  </FormProvider>
                </div>
                <Link href={ENDPOINTS.DOMESTIC_MEETING_SCHEDULER} className='hover:underline flex gap-xsp justify-center items-center' target="_blank">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="brand" className='fill-brand'><path d="M17 3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7V1H9V3H15V1H17V3ZM4 9V19H20V9H4ZM6 13H11V17H6V13Z"></path></svg>
                  <p className='text-base2 font-medium text-brand'>Book a meeting</p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
export default Search;

export async function getServerSideProps(context) {
  const { res } = context;
  try {
    const response = await fetch(
      `${ENDPOINTS.HSN_KEYWORDS_LIST_API}?page=1&limit=100`
    );
    const data = await response.json();
    const rawHsnInfo = data?.data || [];
    const hsnInfo = rawHsnInfo.filter((item) => !HSNsearch_config.includes(item.slug));
    const total = data?.totalCount || 0;
    if (!hsnInfo.length) {
      return { notFound: true };
    }

    return {
      props: {
        hsnInfo,
        total,
      },
    };
  } catch (error) {
    res.statusCode = 500;
    return {
      notFound: true,
    };
  }
}